'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface RegionData {
  id: string;
  name: string;
  icon: string;
  services: string[];
  specialties: string[];
}

interface RegionalCapabilitiesProps {
  regions: RegionData[];
}

const RegionalCapabilities = ({ regions }: RegionalCapabilitiesProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeRegion, setActiveRegion] = useState<string>('');

  useEffect(() => {
    setIsHydrated(true);
    if (regions.length > 0) {
      setActiveRegion(regions[0].id);
    }
  }, [regions]);

  if (!isHydrated) {
    return (
      <section className="py-16 lg:py-24 bg-muted">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
              Regional Service Capabilities
            </h2>
            <p className="font-body text-lg text-muted-foreground">
              Specialized logistics solutions across African markets
            </p>
          </div>
          <div className="bg-card rounded-2xl shadow-lg p-8 animate-pulse">
            <div className="h-96 bg-muted rounded-lg" />
          </div>
        </div>
      </section>
    );
  }

  const currentRegion = regions.find(r => r.id === activeRegion);

  return (
    <section className="py-16 lg:py-24 bg-muted">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-foreground mb-4">
            Regional Service Capabilities
          </h2>
          <p className="font-body text-lg text-muted-foreground">
            Specialized logistics solutions across African markets
          </p>
        </div>
        
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {regions.map((region) => (
              <button
                key={region.id}
                onClick={() => setActiveRegion(region.id)}
                className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-body font-semibold transition-all duration-300 ${
                  activeRegion === region.id
                    ? 'bg-gradient-to-r from-golden-light to-golden-dark text-primary shadow-golden-md'
                    : 'bg-card text-muted-foreground border border-border hover:border-golden-light hover:text-foreground'
                }`}
              >
                <Icon name={region.icon as any} size={20} />
                <span>{region.name}</span>
              </button>
            ))}
          </div>
          
          {currentRegion && (
            <div className="bg-card rounded-2xl shadow-lg p-8 border border-border">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="font-headline font-bold text-2xl text-foreground mb-6 flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-golden-light to-golden-dark flex items-center justify-center">
                      <Icon name="TruckIcon" size={24} className="text-primary" />
                    </div>
                    <span>Available Services</span>
                  </h3>
                  <div className="space-y-3">
                    {currentRegion.services.map((service, index) => (
                      <div key={index} className="flex items-center space-x-3 p-4 bg-muted rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-golden-light to-golden-dark flex items-center justify-center flex-shrink-0">
                          <Icon name="CheckIcon" size={16} className="text-primary" />
                        </div>
                        <span className="font-body text-foreground">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="font-headline font-bold text-2xl text-foreground mb-6 flex items-center space-x-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-golden-light to-golden-dark flex items-center justify-center">
                      <Icon name="StarIcon" size={24} className="text-primary" />
                    </div>
                    <span>Regional Specialties</span>
                  </h3>
                  <div className="space-y-3">
                    {currentRegion.specialties.map((specialty, index) => (
                      <div key={index} className="flex items-start space-x-3 p-4 bg-gradient-to-r from-golden-light/10 to-golden-dark/10 rounded-lg border border-golden-mid/30">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-golden-light to-golden-dark flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Icon name="SparklesIcon" size={16} className="text-primary" />
                        </div>
                        <span className="font-body text-foreground">{specialty}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default RegionalCapabilities;